import moment, { Moment, MomentInput } from 'moment';
/**
 * All input dates are expected to be in MILISECONDS and not seconds
 *
 */
export const formatFullDate = (date: MomentInput): string => {
  return moment(date).format('MMM D[, ]YYYY');
};

export const formatFullDateWithTime = (date: number | string | Date): string => {
  return moment(date).format('MMM D[, ]YYYY, kk:mm');
};

export const formatDateWithTime = (date: number | string | Date): string => {
  return moment(date).format('MMM D, kk:mm');
};

/**
 * "Today" / "Yesterday" for recent dates, else "Jun 8" (same year) or
 * "Jun 8, 2024". Pass `withTime` to append a 12-hour time, e.g. "Today, 3:45 PM".
 */
export const calendarTime = (date: MomentInput, { withTime = false }: { withTime?: boolean } = {}): string => {
  const timeSuffix = withTime ? ', h:mm A' : '';
  // Plain date for anything older than yesterday — overrides calendar()'s "Last Tuesday" default.
  const absoluteDate = function (this: Moment, now?: MomentInput): string {
    const dateFormat = this.year() === moment(now).year() ? 'MMM D' : 'MMM D, YYYY';
    return `${dateFormat}${timeSuffix}`;
  };
  return moment(date).calendar(null, {
    sameDay: `[Today]${timeSuffix}`,
    lastDay: `[Yesterday]${timeSuffix}`,
    lastWeek: absoluteDate,
    sameElse: absoluteDate,
  });
};

export const formatFullYear = (date: number): string => {
  return moment(date).format('YYYY');
};

export const toBackendFormat = (date: number | Date | Moment): string => {
  return moment(date).format('YYYY-MM-DD');
};

export const monthAndFullYear = (date: string): string => {
  return moment(date).format('MMM YYYY');
};

export const formatDate = ({ date, format }: { date: MomentInput; format: string }): string => {
  return moment(date).format(format);
};

export const humanizeSeconds = (seconds?: number): string => {
  return moment.duration(seconds, 'seconds').humanize();
};

// Format an elapsed duration in milliseconds as a "mm:ss" string (e.g. 65000 → "01:05").
// Caps at 59:59
export const formatElapsedMmSs = (elapsedMs: number): string => {
  return moment.utc(Math.max(0, elapsedMs)).format('mm:ss');
};

/**
 * Format a duration (in seconds) as "Xh Ym Zs", dropping the hours segment when zero.
 *
 * Handles any magnitude — unlike {@link formatElapsedMmSs} it does NOT cap at an hour.
 *
 * @param seconds - Duration in seconds. Negative values are clamped to 0.
 * @returns e.g. `84 → "1m 24s"`, `3605 → "1h 0m 5s"`, `13500 → "3h 45m 0s"`.
 */
export const formatDuration = (seconds: number): string => {
  const duration = moment.duration(Math.max(0, seconds), 'seconds');
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();
  const secs = duration.seconds();
  return hours > 0 ? `${hours}h ${minutes}m ${secs}s` : `${minutes}m ${secs}s`;
};

export const toMoment = (time: Moment | null | string, ...args: any): moment.Moment => {
  if (time === undefined || time === null) {
    return moment();
  }
  return moment(time, args);
};

// will return '4 month' (without the ago / in)
export const fromNowShort = (time: number | string): string => {
  return moment(time).fromNow(true);
};

// Format Unix timestamp (in seconds)
export const formatUnixTimestamp = (timestamp: number, format: string): string => {
  return moment.unix(timestamp).format(format);
};

// Format date in UTC
export const formatUTC = (timestamp: number, format: string): string => {
  return moment.unix(timestamp).utc().format(format);
};

// Format offset in hours to GMT string (e.g., GMT+05:30)
export const formatTimezoneOffset = (offsetInHours: number): string => {
  if (!offsetInHours) {
    return '';
  }
  const hour = moment.utc(Math.abs(offsetInHours) * 3600 * 1000).format('HH:mm');
  return `GMT${offsetInHours > 0 ? '+' : '-'}${hour}`;
};

type TimeAsStringFormat = 'normal' | 'short' | 'time' | 'datetime' | 'custom';

// Format timestamp with predefined or custom format
export const timeAsString = (
  timestamp: number | string,
  format?: TimeAsStringFormat,
  customFormat?: string,
): string => {
  const formatFn =
    typeof timestamp === 'number'
      ? (fmt: string) => formatUnixTimestamp(timestamp, fmt)
      : (fmt: string) => formatDate({ date: timestamp, format: fmt });

  switch (format) {
    case 'normal':
      return formatFn('MMM DD, YYYY');
    case 'short':
      return formatFn('MMM DD');
    case 'time':
      return formatFn('h:mm a');
    case 'datetime':
      return formatFn('MMM DD, YYYY, h:mm a');
    case 'custom':
      return formatFn(customFormat!);
    default:
      return formatFn('');
  }
};

type TranslateFn = (args: { id: string }) => string;

// Format Unix timestamp as year (UTC), with optional translation fallback
export const timeAsYear = (timestamp?: number, isEnd = false, translate?: TranslateFn): string => {
  return timestamp ? formatUTC(timestamp, 'YYYY') : isEnd ? (translate?.({ id: 'DATES_NOW' }) ?? '') : '';
};

// Format Unix timestamp range as year or month+year range
export const timeAsYearRange = ({
  beginning,
  end,
  showMonths = false,
  translate,
}: {
  beginning: number;
  end?: number;
  showMonths?: boolean;
  translate: (args: { id: string }) => string;
}): string => {
  const format = showMonths ? 'MMM YYYY' : 'YYYY';
  const start = formatUnixTimestamp(beginning, format);
  const endStr = end ? formatUnixTimestamp(end, format) : translate({ id: 'DATES_NOW' });
  return `${start} - ${endStr}`;
};

// Format date as full month name (e.g., "January")
export const formatMonth = (date: Date | string | number): string => {
  return moment(date).format('MMMM');
};

// Format date as weekday abbreviation (e.g., "Mo", "Tu")
export const formatWeekdayAbbreviation = (date: Date | string | number): string => {
  return moment(date).format('dd');
};
